import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import api from '@/lib/api';
import { 
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Droplets,
  Flame,
  Activity,
  Target,
  DollarSign,
  Sparkles,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ReferenceLine
} from 'recharts';
import { cn } from '@/lib/utils';

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toFixed(0) || '0';
};

const OperatorDetail = () => {
  const { id } = useParams();
  const { priceDeck } = useStore();
  const [operator, setOperator] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forecastLoading, setForecastLoading] = useState(false);

  useEffect(() => {
    const fetchOperator = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/operators/${id}`);
        setOperator(res.data);
      } catch (error) {
        console.error('Error fetching operator:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOperator();
  }, [id]);

  useEffect(() => {
    const fetchForecast = async () => {
      if (!id) return;
      try {
        setForecastLoading(true);
        const res = await api.get(`/forecast/operator/${id}?price_deck=${priceDeck}`);
        setForecast(res.data);
      } catch (error) {
        console.error('Error fetching forecast:', error);
      } finally {
        setForecastLoading(false);
      }
    };
    fetchForecast();
  }, [id, priceDeck]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!operator) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Operator not found</p>
        <Link to="/operators">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Operators
          </Button>
        </Link>
      </div>
    );
  }

  const productionData = operator.monthly_production?.map((p, idx) => ({
    month: p.month,
    oil: p.oil,
    gas: p.gas / 6,
  })) || [];

  // Combine historical and forecast data
  const combinedData = forecast ? [
    ...forecast.historical.months.map((month, idx) => ({
      month,
      oil: forecast.historical.oil[idx],
      type: 'historical'
    })),
    ...forecast.forecast.months.map((month, idx) => ({
      month,
      oil: forecast.forecast.oil[idx],
      type: 'forecast'
    }))
  ] : [];

  const revenueData = forecast?.forecast.months.map((month, idx) => ({
    month,
    revenue: forecast.forecast.total_revenue[idx],
    oil: forecast.forecast.oil_revenue[idx],
    gas: forecast.forecast.gas_revenue[idx],
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/operators">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-wide uppercase">
              {operator.name}
            </h1>
            <p className="text-sm text-muted-foreground font-mono mt-1">
              {operator.normalized_name}
            </p>
          </div>
        </div>
        <Badge 
          variant="outline" 
          className={cn(
            "font-mono text-sm px-4 py-2",
            operator.momentum_score >= 70 
              ? "border-success/50 text-success" 
              : operator.momentum_score >= 40 
                ? "border-primary/50 text-primary"
                : "border-danger/50 text-danger"
          )}
        >
          Momentum: {operator.momentum_score?.toFixed(0)}
        </Badge>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Droplets className="w-4 h-4 text-primary" />
              <span className="text-xs uppercase tracking-wider">Oil Production</span>
            </div>
            <p className="text-2xl font-heading font-bold">
              {formatNumber(operator.total_oil_production)} BBL
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Flame className="w-4 h-4 text-secondary" />
              <span className="text-xs uppercase tracking-wider">Gas Production</span>
            </div>
            <p className="text-2xl font-heading font-bold">
              {formatNumber(operator.total_gas_production)} MCF
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Activity className="w-4 h-4 text-success" />
              <span className="text-xs uppercase tracking-wider">Active Wells</span>
            </div>
            <p className="text-2xl font-heading font-bold">
              {operator.active_wells}
              <span className="text-sm text-muted-foreground ml-2">/ {operator.total_wells}</span>
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              {operator.growth_rate >= 0 ? (
                <TrendingUp className="w-4 h-4 text-success" />
              ) : (
                <TrendingDown className="w-4 h-4 text-danger" />
              )}
              <span className="text-xs uppercase tracking-wider">Growth Rate</span>
            </div>
            <p className={cn(
              "text-2xl font-heading font-bold",
              operator.growth_rate >= 0 ? "text-success" : "text-danger"
            )}>
              {operator.growth_rate >= 0 ? '+' : ''}{operator.growth_rate?.toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Production History */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-4 bg-white/[0.02]">
            <CardTitle className="text-sm font-heading uppercase tracking-widest">
              Production History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={productionData}>
                  <defs>
                    <linearGradient id="oilGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                  />
                  <YAxis 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    tickFormatter={formatNumber}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0F0F10', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      fontFamily: 'IBM Plex Mono',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [formatNumber(value), 'BBL']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="oil" 
                    stroke="#F59E0B" 
                    fill="url(#oilGrad)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Forecast */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-4 bg-white/[0.02]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading uppercase tracking-widest flex items-center gap-2">
                <Target className="w-4 h-4" />
                12-Month Forecast
              </CardTitle>
              {forecastLoading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {forecast ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={combinedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    />
                    <YAxis 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      tickFormatter={formatNumber}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0F0F10', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '12px'
                      }}
                      formatter={(value, name, props) => [
                        formatNumber(value), 
                        props.payload.type === 'forecast' ? 'Forecast (BBL)' : 'Actual (BBL)'
                      ]}
                    />
                    <ReferenceLine 
                      x={forecast.historical.months[forecast.historical.months.length - 1]} 
                      stroke="#52525B" 
                      strokeDasharray="3 3" 
                      label={{ value: 'Now', fill: '#A1A1AA', fontSize: 10 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="oil" 
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      dot={false}
                      strokeDasharray={(d) => d.type === 'forecast' ? '5 5' : '0'}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Projection & AI Insight */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2 bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-4 bg-white/[0.02]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading uppercase tracking-widest flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Revenue Projection
              </CardTitle>
              <Badge variant="outline" className="border-primary/50 text-primary font-mono text-xs">
                WTI ${priceDeck}/bbl
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {forecast ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    />
                    <YAxis 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      tickFormatter={(v) => `$${formatNumber(v)}`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0F0F10', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '12px'
                      }}
                      formatter={(value) => [`$${formatNumber(value)}`, 'Revenue']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#10B981" 
                      fill="url(#revGrad)" 
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Insight */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-4 bg-white/[0.02]">
            <CardTitle className="text-sm font-heading uppercase tracking-widest flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              AI Insight
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {forecast?.ai_insight ? (
              <div className="space-y-4">
                <p className="text-sm text-foreground leading-relaxed">
                  {forecast.ai_insight}
                </p>
                <div className="pt-4 border-t border-white/5">
                  <p className="text-xs text-muted-foreground">
                    Decline Model: <span className="text-foreground font-mono">{forecast.decline_params?.oil?.model}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Initial Rate: <span className="text-foreground font-mono">{formatNumber(forecast.decline_params?.oil?.qi)} BBL/mo</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Decline Rate: <span className="text-foreground font-mono">{(forecast.decline_params?.oil?.di * 100).toFixed(1)}%/mo</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OperatorDetail;
